import { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, Timestamp, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Package, Plus, Search, PenSquare, ExternalLink, Euro, ArrowUpIcon, ArrowDownIcon, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import AddPricebenchModal from '../../../components/pricebench/AddPricebenchModal';
import ViewUrlModal from '../../../components/pricebench/ViewUrlModal';
import EditPriceModal from '../../../components/pricebench/EditPriceModal';
import PricebenchStats from '../../../components/pricebench/PricebenchStats';
import { updateCompetitorPrice } from '../../../services/scraping';
import { competitors } from '../../../config/competitors';

// Mapeamento dos competidores para o formato necessário
const mappedCompetitors = {
  [competitors.CONTINENTE.code]: {
    logo: competitors.CONTINENTE.logo,
    name: competitors.CONTINENTE.name,
    active: competitors.CONTINENTE.available
  },
  [competitors.KUANTOKUSTA.code]: {
    logo: competitors.KUANTOKUSTA.logo,
    name: competitors.KUANTOKUSTA.name,
    active: competitors.KUANTOKUSTA.available
  },
  [competitors.WORTEN.code]: {
    logo: competitors.WORTEN.logo,
    name: competitors.WORTEN.name,
    active: competitors.WORTEN.available
  },
  [competitors.AMAZON.code]: {
    logo: competitors.AMAZON.logo,
    name: competitors.AMAZON.name,
    active: competitors.AMAZON.available
  }
} as const;

interface PricebenchItem {
  id: string;
  productId: string;
  productName: string;
  productEan: string;
  regularPrice: number;
  proPlusPrice?: number;
  productImage?: string;
  isProPlus: boolean;
  competitorName: string;
  competitorCode: string;
  competitorUrl: string;
  competitorPrice?: number;
  lastUpdate?: Timestamp;
  promotionalPeriod?: {
    startDate: string;
    endDate: string;
  };
  salePrice?: number;
  sku?: string;
}

// Função auxiliar para formatar moeda
function formatCurrency(value: number | undefined | null): string {
  if (value === undefined || value === null) return '€0.00';
  return `€${value.toFixed(2)}`;
}

// Função para verificar se um produto está em promoção
const isOnSale = (item: PricebenchItem): boolean => {
  if (!item?.promotionalPeriod) return false;
  
  const now = new Date();
  const startDate = new Date(item.promotionalPeriod.startDate);
  const endDate = new Date(item.promotionalPeriod.endDate);
  
  return now >= startDate && now <= endDate;
};

// Função para obter o preço atual do produto
const getCurrentPrice = (item: PricebenchItem): number => {
  if (!item) return 0;
  
  // Se estiver em período promocional, retorna o preço promocional
  if (isOnSale(item)) {
    return item.salePrice || 0;
  }
  
  // Se não estiver em promoção, retorna o preço regular
  return item.regularPrice || 0;
};

export default function Pricebench() {
  const [items, setPricebenchItems] = useState<PricebenchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [priceFilter, setPriceFilter] = useState<'todos' | 'baratos' | 'caros'>(() => {
    return (localStorage.getItem('pricebenchPriceFilter') as 'todos' | 'baratos' | 'caros') || 'todos';
  });
  const [competitorFilter, setCompetitorFilter] = useState<string>(() => {
    return localStorage.getItem('pricebenchCompetitorFilter') || 'todos';
  });
  const itemsPerPage = 25;
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewUrlModal, setShowViewUrlModal] = useState(false);
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PricebenchItem | null>(null);
  const [viewUrl, setViewUrl] = useState<{
    url: string;
    productName?: string;
    productImage?: string;
    competitorName?: string;
    competitorPrice?: number;
  } | null>(null);
  const [loadingPrices, setLoadingPrices] = useState<{ [key: string]: boolean }>({});
  const { currentUser } = useAuth();

  // Componente para exibir o preço com indicador de promoção
  const PriceDisplay = ({ item }: { item: PricebenchItem }) => {
    const isPromotional = isOnSale(item);
    const currentPrice = getCurrentPrice(item);
    
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          <div className="flex items-center">
            {isPromotional ? (
              <>
                <span>{formatCurrency(currentPrice)}</span>
                <div className="text-xs text-gray-500 line-through ml-2">
                  {formatCurrency(item.regularPrice)}
                </div>
              </>
            ) : (
              <span>{formatCurrency(currentPrice)}</span>
            )}
          </div>
          {item.competitorPrice && (
            <span 
              className={`text-sm ml-1 ${currentPrice < item.competitorPrice ? 'text-green-500' : 'text-red-500'}`}
              title={`Nosso preço está ${currentPrice < item.competitorPrice ? 'menor' : 'maior'} que o concorrente`}
            >
              {currentPrice < item.competitorPrice ? (
                <ArrowDownIcon className="w-4 h-4" />
              ) : (
                <ArrowUpIcon className="w-4 h-4" />
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Componente de edição de preço
  const PriceEditor = ({ 
    initialPrice, 
    onSave, 
    className = "" 
  }: { 
    initialPrice: number; 
    onSave: (newPrice: number) => Promise<void>;
    className?: string;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [price, setPrice] = useState(initialPrice?.toString() || '0');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);

    const handleSave = async () => {
      const newPrice = parseFloat(price);
      if (!isNaN(newPrice) && newPrice >= 0) {
        try {
          await onSave(newPrice);
          setIsEditing(false);
        } catch (error) {
          toast.error('Erro ao atualizar o preço');
        }
      } else {
        toast.error('Preço inválido');
        setPrice(initialPrice?.toString() || '0');
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        setPrice(initialPrice?.toString() || '0');
        setIsEditing(false);
      }
    };

    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          min="0"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`w-20 px-2 py-1 border rounded ${className}`}
        />
      );
    }

    return (
      <button
        onClick={() => setIsEditing(true)}
        className={`hover:bg-gray-100 rounded px-2 py-1 -ml-2 ${className}`}
      >
        {formatCurrency(parseFloat(price))}
      </button>
    );
  };

  const handleUpdatePrices = async (productId: string, updates: {
    regularPrice?: number;
    proPlusPrice?: number;
    salePrice?: number;
    promotionalPeriod?: {
      startDate: string;
      endDate: string;
    } | null;
  }) => {
    try {
      const productRef = doc(db, 'products', productId);
      
      const updateData: any = {
        updatedAt: serverTimestamp(),
        ...updates
      };
      
      await updateDoc(productRef, updateData);

      // Atualiza o estado local
      setPricebenchItems(items.map(item => {
        if (item.productId === productId) {
          return {
            ...item,
            ...updates
          };
        }
        return item;
      }));

      toast.success('Preços atualizados com sucesso');
    } catch (error) {
      console.error('Erro ao atualizar preços:', error);
      toast.error('Erro ao atualizar preços');
      throw error;
    }
  };

  const loadPricebenchItems = async () => {
    if (!currentUser?.companyId) {
      setLoading(false);
      toast.error('Erro: ID da empresa não encontrado');
      return;
    }

    setLoading(true);
    try {
      // Primeiro, carregar todos os items do pricebench
      const pricebenchQuery = query(
        collection(db, 'pricebench'),
        where('companyId', '==', currentUser.companyId)
      );
      const pricebenchSnapshot = await getDocs(pricebenchQuery);
      const pricebenchItems = pricebenchSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PricebenchItem[];

      // Para cada item do pricebench, buscar informações adicionais do produto
      const updatedItems = await Promise.all(
        pricebenchItems.map(async (pricebenchItem) => {
          try {
            const productRef = doc(db, 'products', pricebenchItem.productId);
            const productSnap = await getDoc(productRef);
            
            if (productSnap.exists()) {
              const productData = productSnap.data();
              
              // Verificar se o produto pertence à mesma empresa
              if (productData.companyId !== currentUser.companyId) {
                console.warn('Produto não pertence à empresa:', pricebenchItem.productId);
                return null;
              }
              
              return {
                ...pricebenchItem,
                productImage: productData.mainImage || productData.baselinkerMainImage,
                productName: productData.name,
                proPlusPrice: productData.proPlusPrice,
                isProPlus: productData.isProPlus,
                promotionalPeriod: {
                  startDate: productData.promotionalPeriod?.startDate,
                  endDate: productData.promotionalPeriod?.endDate
                },
                regularPrice: productData.regularPrice,
                salePrice: productData.salePrice,
                sku: productData.sku
              };
            }
            return null;
          } catch (error) {
            console.error('Error loading product:', error);
            return null;
          }
        })
      );

      // Filtrar items nulos (produtos que não pertencem à empresa)
      const filteredItems = updatedItems.filter(item => item !== null) as PricebenchItem[];
      setPricebenchItems(filteredItems);
    } catch (err) {
      console.error('Erro ao carregar items:', err);
      toast.error('Erro ao carregar items do Pricebench');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricebenchItems();
  }, [currentUser]);

  // Função para calcular estatísticas dos produtos
  const calculateStats = () => {
    // Criar um Set de EANs únicos
    const uniqueEans = new Set(items.map(item => item.productEan));
    const totalProducts = uniqueEans.size;

    const cheaperProducts = items.filter(item => {
      const ourPrice = item.isProPlus ? (item.proPlusPrice || item.regularPrice) : item.regularPrice;
      return item.competitorPrice && ourPrice < item.competitorPrice;
    });
    const expensiveProducts = items.filter(item => {
      const ourPrice = item.isProPlus ? (item.proPlusPrice || item.regularPrice) : item.regularPrice;
      return item.competitorPrice && ourPrice > item.competitorPrice;
    });

    return {
      total: totalProducts,
      cheaper: cheaperProducts.length,
      expensive: expensiveProducts.length
    };
  };

  // Calcular índices para paginação
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);

  // Calcular número total de páginas
  const totalPages = Math.ceil(items.length / itemsPerPage);

  // Filtrar itens baseado na busca, filtro de preço e filtro de concorrente
  const filteredItems = items.filter(item => {
    // Primeiro aplicar filtro de busca
    const matchesSearch = searchTerm === '' || (
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.productEan.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!matchesSearch) return false;

    // Aplicar filtro de concorrente
    if (competitorFilter !== 'todos' && item.competitorCode !== competitorFilter) {
      return false;
    }

    // Depois aplicar filtro de preço
    if (priceFilter === 'todos') return true;

    const ourPrice = item.isProPlus ? (item.proPlusPrice || item.regularPrice) : item.regularPrice;
    if (!item.competitorPrice) return false;

    if (priceFilter === 'baratos') {
      return ourPrice < item.competitorPrice;
    } else if (priceFilter === 'caros') {
      return ourPrice > item.competitorPrice;
    }

    return true;
  });

  // Função para lidar com a adição/edição de um item
  const handleAddItem = async (newItem: Omit<PricebenchItem, 'id'>, itemId?: string) => {
    try {
      if (itemId) {
        // Se tiver ID, é uma edição
        const docRef = doc(db, 'pricebench', itemId);
        await updateDoc(docRef, {
          ...newItem,
          lastUpdate: serverTimestamp()
        });
        toast.success('Produto atualizado com sucesso!');
      } else {
        // Se não tiver ID, é uma adição
        await addDoc(collection(db, 'pricebench'), {
          ...newItem,
          companyId: currentUser?.companyId,
          lastUpdate: serverTimestamp()
        });
        toast.success('Produto adicionado com sucesso!');
      }
      loadPricebenchItems(); // Recarrega a lista
      setShowAddModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      toast.error('Erro ao salvar produto. Tente novamente.');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Package className="h-6 w-6 text-blue-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Total de Produtos</h3>
            </div>
            <span className="text-2xl font-bold text-blue-500">{calculateStats().total}</span>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Produtos monitorados no comparador
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Euro className="h-6 w-6 text-green-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Preços Menores</h3>
            </div>
            <span className="text-2xl font-bold text-green-500">
              {items.filter(item => item.competitorPrice && getCurrentPrice(item) < item.competitorPrice).length}
            </span>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Produtos com preços menores que a concorrência
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Euro className="h-6 w-6 text-red-500 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Preços Maiores</h3>
            </div>
            <span className="text-2xl font-bold text-red-500">
              {items.filter(item => item.competitorPrice && getCurrentPrice(item) > item.competitorPrice).length}
            </span>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Produtos com preços maiores que a concorrência
          </p>
        </div>

        <PricebenchStats />
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="relative flex-1 md:w-96">
            <input
              type="text"
              placeholder="Pesquisar por nome ou EAN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          
          <select
            value={priceFilter}
            onChange={(e) => {
              const newValue = e.target.value as 'todos' | 'baratos' | 'caros';
              setPriceFilter(newValue);
              localStorage.setItem('pricebenchPriceFilter', newValue);
              setCurrentPage(1); // Resetar para primeira página ao mudar o filtro
            }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="todos">Todos os Produtos</option>
            <option value="baratos">Mais Baratos que Concorrência</option>
            <option value="caros">Mais Caros que Concorrência</option>
          </select>

          <select
            value={competitorFilter}
            onChange={(e) => {
              const newValue = e.target.value;
              setCompetitorFilter(newValue);
              localStorage.setItem('pricebenchCompetitorFilter', newValue);
              setCurrentPage(1); // Resetar para primeira página ao mudar o filtro
            }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="todos">Todos os Concorrentes</option>
            {Object.entries(mappedCompetitors).map(([code, competitor]) => (
              competitor.active && (
                <option key={code} value={code}>
                  {competitor.name}
                </option>
              )
            ))}
          </select>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar Produto
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PRO+
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Concorrente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço Concorrente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      Carregando...
                    </div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    Nenhum produto encontrado.
                  </td>
                </tr>
              ) : (
                filteredItems.slice(indexOfFirstItem, indexOfLastItem).map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <img
                          src={item.productImage || '/placeholder.png'}
                          alt={item.productName}
                          className="w-12 h-12 object-contain"
                        />
                        <div className="flex flex-col justify-center">
                          <a 
                            href={`https://www.emtuacasa.pt/product/${item.productId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-gray-900 hover:text-primary hover:underline flex items-center gap-1"
                          >
                            {item.productName}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="cursor-pointer" onClick={() => {
                        setSelectedItem(item);
                        setShowEditPriceModal(true);
                      }}>
                        <PriceDisplay item={item} />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 cursor-pointer" onClick={() => {
                        setSelectedItem(item);
                        setShowEditPriceModal(true);
                      }}>
                        {item.proPlusPrice !== undefined && (
                          <>
                            <span>{formatCurrency(item.proPlusPrice)}</span>
                            {getCurrentPrice(item) !== undefined && (
                              <span 
                                className={`text-sm ml-1 ${item.proPlusPrice < getCurrentPrice(item) ? 'text-green-500' : 'text-red-500'}`}
                                title={`Preço PRO+ está ${item.proPlusPrice < getCurrentPrice(item) ? 'menor' : 'maior'} que o preço regular`}
                              >
                                {item.proPlusPrice < getCurrentPrice(item) ? (
                                  <ArrowDownIcon className="w-4 h-4" />
                                ) : (
                                  <ArrowUpIcon className="w-4 h-4" />
                                )}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {item.competitorCode && mappedCompetitors[item.competitorCode] && (
                          <>
                            <img 
                              src={mappedCompetitors[item.competitorCode].logo}
                              alt={item.competitorName}
                              className="w-6 h-6 object-contain"
                            />
                            <span>
                              {item.competitorName}
                              {!mappedCompetitors[item.competitorCode].active && (
                                <span className="ml-1 text-xs text-gray-500">(brevemente)</span>
                              )}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {item.competitorPrice ? (
                          <div className="group relative inline-block">
                            <div className="flex items-center gap-1">
                              <span>{formatCurrency(item.competitorPrice)}</span>
                            </div>
                            {/* Tooltip com data de atualização */}
                            <div className="absolute z-50 invisible group-hover:visible bg-gray-800 text-white text-xs rounded py-2 px-3 whitespace-nowrap -top-10 left-1/2 transform -translate-x-1/2 min-w-[200px] text-center">
                              <div>Última atualização:</div>
                              <div className="font-medium">
                                {item.lastUpdate ? new Date(item.lastUpdate.toDate()).toLocaleString('pt-PT', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'Nunca'}
                              </div>
                              {/* Seta do tooltip */}
                              <div className="absolute w-2 h-2 bg-gray-800 transform rotate-45 left-1/2 -translate-x-1/2 -bottom-1"></div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Pendente</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={async () => {
                            try {
                              // Prevenir múltiplos cliques enquanto está carregando
                              if (loadingPrices[item.id]) return;
                              
                              // Marcar como loading
                              setLoadingPrices(prev => ({ ...prev, [item.id]: true }));
                              
                              const newPrice = await updateCompetitorPrice(item.competitorUrl);
                              if (newPrice) {
                                // Atualizar o preço no Firestore
                                const pricebenchRef = doc(db, 'pricebench', item.id);
                                await updateDoc(pricebenchRef, {
                                  competitorPrice: newPrice,
                                  lastUpdate: serverTimestamp()
                                });
                                
                                // Atualizar apenas o item específico na lista local
                                setPricebenchItems(prevItems => 
                                  prevItems.map(prevItem => 
                                    prevItem.id === item.id 
                                      ? { ...prevItem, competitorPrice: newPrice }
                                      : prevItem
                                  )
                                );
                                
                                toast.success('Preço atualizado com sucesso!');
                              } else {
                                toast.error('Não foi possível encontrar o preço');
                              }
                            } catch (error) {
                              console.error('Erro ao atualizar preço:', error);
                              toast.error('Erro ao atualizar preço');
                            } finally {
                              // Remover loading
                              setLoadingPrices(prev => ({ ...prev, [item.id]: false }));
                            }
                          }}
                          disabled={loadingPrices[item.id]}
                          className={`text-primary hover:text-primary/90 flex items-center tooltip ${
                            loadingPrices[item.id] ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          data-tip="Atualizar Preço"
                        >
                          {loadingPrices[item.id] ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                          ) : (
                            <Euro className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setViewUrl({
                              url: item.competitorUrl,
                              productName: item.productName,
                              productImage: item.productImage,
                              competitorName: item.competitorName,
                              competitorPrice: item.competitorPrice
                            });
                            setShowViewUrlModal(true);
                          }}
                          className="text-primary hover:text-primary/90 flex items-center tooltip"
                          data-tip="Ver Produto"
                        >
                          <ExternalLink className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedItem(item);
                            setShowAddModal(true);  // Voltar para o AddPricebenchModal
                          }}
                          className="text-primary hover:text-primary/90 flex items-center tooltip"
                          data-tip="Editar"
                        >
                          <PenSquare className="h-5 w-5" />
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Tem certeza que deseja remover este item do Pricebench?')) {
                              try {
                                const docRef = doc(db, 'pricebench', item.id);
                                await deleteDoc(docRef);
                                toast.success('Item removido com sucesso');
                                loadPricebenchItems(); // Recarrega a lista
                              } catch (error) {
                                console.error('Erro ao remover item:', error);
                                toast.error('Erro ao remover item');
                              }
                            }
                          }}
                          className="text-red-500 hover:text-red-600 flex items-center tooltip"
                          data-tip="Remover"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      <div className="mt-4 flex justify-center items-center gap-2">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
        >
          Anterior
        </button>
        <span className="mx-2">
          Página {currentPage} de {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50"
        >
          Próxima
        </button>
      </div>

      {/* Modais */}
      {showAddModal && (
        <AddPricebenchModal
          onClose={() => {
            setShowAddModal(false);
            setSelectedItem(null);
          }}
          onAdd={handleAddItem}
          selectedItem={selectedItem}
          competitors={mappedCompetitors}
        />
      )}

      {showViewUrlModal && viewUrl && (
        <ViewUrlModal
          isOpen={showViewUrlModal}
          onClose={() => setShowViewUrlModal(false)}
          url={viewUrl.url}
          productName={viewUrl.productName}
          productImage={viewUrl.productImage}
          competitorName={viewUrl.competitorName}
          competitorPrice={viewUrl.competitorPrice}
        />
      )}

      {showEditPriceModal && selectedItem && (
        <EditPriceModal
          isOpen={showEditPriceModal}
          onClose={() => {
            setShowEditPriceModal(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
          onSave={handleUpdatePrices}
        />
      )}
    </div>
  );
}
